import React, { useRef, useState, useEffect, LegacyRef } from 'react';
import ChevronS2Up from 'plaid-threads/Icons/ChevronS2Up';
import ChevronS2Down from 'plaid-threads/Icons/ChevronS2Down';
import Touchable from 'plaid-threads/Touchable';
import cx from 'classnames';
import { SchemaObject, ParameterObject } from 'openapi3-ts';

import Layout from '../../Layout';

import { SchemaObjectEntry } from '../index';
import Table from './Table';
import { isExpandable, normalizeSchemaForRendering } from './schemaUtils';

import styles from '../index.module.scss';

interface Props {
  schemaName: string;
  route: string;
  entries: Array<SchemaObjectEntry>;
  pathEntries?: Array<ParameterObject>;
  children?: React.ReactNode;
  headerText?: React.ReactNode;
  description?: React.ReactNode;
  required?: Array<string>;
  expandAll: boolean;
  tableOnly?: boolean;
  simplify?: boolean;
  expandText?: string;
  hideText?: string;
  unhideFlag?: string;
  type?: string;
  isRequest?: boolean;
  responseExamples?: SchemaObject['examples'];
  stringCode?: string;
  exchangeSpec?: string;
  coreExchangeSpec?: string;
  onSchemaRowLineNumbers?: (lineStart: number | null) => void;
  schema?: SchemaObject;
}

interface HeaderProps extends Props {
  expandAll: boolean;
  setExpandAll: (boolean) => void;
  showToggle: boolean;
}

const Header = React.forwardRef<HTMLDivElement, HeaderProps>((props, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.tableHeaderWrapper,
      props.tableOnly && styles.tableHeaderWrapperNoPadding,
      styles.stickyHeader,
    )}
  >
    <div
      className={cx(
        styles.tableHeaderButtonWrapper,
        props.headerText == null && styles.noHeader,
      )}
    >
      <p className={styles.tableHeader}>
        <b>{props.headerText}</b>
      </p>
      {props.showToggle && (
        <Touchable
          className={cx(styles.expandAllButton, 'hide-for-aeo')}
          onClick={() => props.setExpandAll(!props.expandAll)}
        >
          {props.expandAll ? 'Collapse all' : 'Expand all'}

          {props.expandAll ? <ChevronS2Up /> : <ChevronS2Down />}
        </Touchable>
      )}
    </div>
  </div>
));

const SchemaLayout: React.FC<Props> = (props: Props) => {
  const [expandAll, setExpandAll] = React.useState(Boolean(props.expandAll));
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, []);

  // Stops at the first expandable entry rather than normalizing every schema on
  // the page, which is recursive and runs on each render.
  const showToggle = props.entries.some((entry) =>
    isExpandable(normalizeSchemaForRendering(entry[1])),
  );
  const name = `endpoint: ${props.schemaName}`;
  return (
    <div
      className={cx(styles.schema, props.isRequest && styles.request)}
      data-algolia='schema'
    >
      {props.description}
      <span
        className={cx(styles.schemaRouteHidden, 'hide-for-aeo')}
        data-algolia='schemaRouteHidden'
      >
        {props.route}
      </span>
      {props.tableOnly || props.simplify ? (
        <div className={styles.layoutWrapper}>
          <div>
            <Header
              {...props}
              expandAll={expandAll}
              setExpandAll={setExpandAll}
              showToggle={showToggle}
            />
            <Table
              exchangeSpec={props.exchangeSpec}
              coreExchangeSpec={props.coreExchangeSpec}
              schemaName={props.schemaName}
              entries={props.entries}
              pathEntries={props.pathEntries}
              required={props.required}
              expandAll={expandAll}
              expandText={props.expandText}
              hideText={props.hideText}
              unhideFlag={props.unhideFlag}
              simplify={props.simplify}
              type={props.type}
              responseExamples={props.responseExamples}
              stringCode={props.stringCode}
              route={props.route}
              onSchemaRowLineNumbers={props.onSchemaRowLineNumbers}
              schema={props.schema}
            />
          </div>
        </div>
      ) : (
        <Layout.SideBySide className={styles.layoutWrapper}>
          <div>
            <Header
              ref={headerRef}
              {...props}
              expandAll={expandAll}
              setExpandAll={setExpandAll}
              showToggle={showToggle}
            />
            <Table
              schemaName={props.schemaName}
              entries={props.entries}
              pathEntries={props.pathEntries}
              required={props.required}
              expandAll={expandAll}
              exchangeSpec={props.exchangeSpec}
              coreExchangeSpec={props.coreExchangeSpec}
              expandText={props.expandText}
              hideText={props.hideText}
              unhideFlag={props.unhideFlag}
              type={props.type}
              responseExamples={props.responseExamples}
              stringCode={props.stringCode}
              route={props.route}
              headerHeight={headerHeight}
              onSchemaRowLineNumbers={props.onSchemaRowLineNumbers}
              schema={props.schema}
            />
          </div>

          <div className={styles.codeBlock}>{props.children}</div>
        </Layout.SideBySide>
      )}
    </div>
  );
};

SchemaLayout.displayName = 'SchemaLayout';

export default SchemaLayout;
